import React, { useState, useEffect, useMemo, useCallback } from "react";
import config from "../../Config";
import axios from "axios";
import Spinner from "react-bootstrap/Spinner";
import StudentMenu from "./StudentMenu";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { NavLink } from "react-router-dom";
import { useParams } from "react-router-dom";
import moment from "moment";
import Pagination from "../General/Pagination";
import ShowStudent from "../Shared/ShowStudent";
import YearsDropDown from "../Shared/YearsDropDown";
import MonthsDropDown from "../Shared/MonthsDropDown";

function parseMoney(v) {
  if (v === null || v === undefined || v === "") return 0;
  const n = typeof v === "number" ? v : parseFloat(String(v).replace(/,/g, ""));
  return Number.isFinite(n) ? n : 0;
}

function formatMoney(n) {
  return parseMoney(n).toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}

/** One row per fee code: latest PostingDate (then highest ID). Avoids double-counting when arrears are rolled into a later month's receivable. */
function getLatestRowPerFeeCode(rows) {
  if (!rows || rows.length === 0) return [];
  const byFee = new Map();
  for (const item of rows) {
    const code = String(item.FeeCode ?? "").trim() || "_";
    const existing = byFee.get(code);
    const t = item.PostingDate ? new Date(item.PostingDate).getTime() : 0;
    const id = item.ID != null ? Number(item.ID) : 0;
    if (!existing) {
      byFee.set(code, item);
      continue;
    }
    const et = existing.PostingDate ? new Date(existing.PostingDate).getTime() : 0;
    const exid = existing.ID != null ? Number(existing.ID) : 0;
    if (t > et || (t === et && id > exid)) {
      byFee.set(code, item);
    }
  }
  return Array.from(byFee.values());
}

function sumLedgerRows(rows) {
  return rows.reduce(
    (acc, item) => ({
      receiveable: acc.receiveable + parseMoney(item.ReceiveableFee),
      received: acc.received + parseMoney(item.ReceivedAmount),
      pending: acc.pending + parseMoney(item.PendingFee),
      arrears: acc.arrears + parseMoney(item.Arrears),
      feePosted: acc.feePosted + parseMoney(item.Fee_Posted),
      concession: acc.concession + parseMoney(item.ConcessionAmount),
    }),
    { receiveable: 0, received: 0, pending: 0, arrears: 0, feePosted: 0, concession: 0 }
  );
}

function computeLedgerTotals(rows) {
  const latestRows = getLatestRowPerFeeCode(rows);
  const current = sumLedgerRows(latestRows);
  const lifetime = sumLedgerRows(rows);
  return {
    /** Unpaid balance per fee head (latest row only — arrears not double-counted). */
    outstanding: current.pending,
    currentArrears: current.arrears,
    /** Cash collected: sum every ledger line (paid months stay on older rows). */
    totalReceived: lifetime.received,
    lifetimeReceivablePosted: lifetime.receiveable,
  };
}

/** Due amount from challan head — latest, or matched by challan no / month-year when filtering. */
function getChallanReceivableAmount(challanList, options = {}) {
  if (!challanList || challanList.length === 0) return null;

  const challanNo = (options.challanNo || "").trim();
  if (challanNo) {
    const ch = challanList.find(
      (c) => (c.Challan_No || "").trim() === challanNo
    );
    return ch ? getChallanDueAmount(ch) : null;
  }

  const month = options.month ? parseInt(options.month, 10) : 0;
  const year = options.year ? parseInt(options.year, 10) : 0;
  if (month > 0 && year > 0) {
    const ch = challanList.find(
      (c) => Number(c.Month) === month && Number(c.Year) === year
    );
    return ch ? getChallanDueAmount(ch) : null;
  }

  return getChallanDueAmount(challanList[0]);
}

/** Due amount on challan head (before/after due date), same rule as bank scroll posting. */
function getChallanDueAmount(ch, asOf = new Date()) {
  const due = parseMoney(ch.Due_Date_Amount);
  const after = parseMoney(ch.After_Due_Date_Amount);
  if (
    ch.Due_Date &&
    moment(asOf).isAfter(moment(ch.Due_Date), "day") &&
    after > 0
  ) {
    return after;
  }
  return due > 0 ? due : after;
}

/**
 * Rows for the most recent fee posting (latest challan if present, else latest calendar month).
 * Used for "current challan" breakdown — not deduped per fee code (all lines belong to one posting).
 */
function getLatestPostingSlice(rows) {
  if (!rows || rows.length === 0) {
    return { rows: [], monthLabel: "", challanNo: "" };
  }

  let latest = rows[0];
  for (const r of rows) {
    const t = r.PostingDate ? new Date(r.PostingDate).getTime() : 0;
    const lt = latest.PostingDate ? new Date(latest.PostingDate).getTime() : 0;
    const id = r.ID != null ? Number(r.ID) : 0;
    const lid = latest.ID != null ? Number(latest.ID) : 0;
    if (t > lt || (t === lt && id > lid)) {
      latest = r;
    }
  }

  const challanNo = (latest.Challan_No || "").trim();
  const slice = challanNo
    ? rows.filter((r) => (r.Challan_No || "").trim() === challanNo)
    : rows.filter((r) => {
        if (!r.PostingDate || !latest.PostingDate) return false;
        const m = moment(r.PostingDate);
        const lm = moment(latest.PostingDate);
        return m.month() === lm.month() && m.year() === lm.year();
      });

  return {
    rows: slice,
    monthLabel: latest.PostingDate
      ? moment(latest.PostingDate).format("MMM YYYY")
      : "",
    challanNo,
  };
}

function StudentLedger2() {
  const params = useParams();
  const studentIdParam = params.id;

  const [AccessKey] = useState(localStorage.getItem("AccessKey"));
  const [UserID] = useState(localStorage.getItem("ID"));
  const [MemberType] = useState(localStorage.getItem("MemberType"));

  const [loading, setLoading] = useState(false);

  const [fullLedger, setFullLedger] = useState([]);
  const [filteredLedger, setFilteredLedger] = useState([]);
  const [filterMonth, setFilterMonth] = useState("");
  const [filterYear, setFilterYear] = useState("");
  const [filterChallanNo, setFilterChallanNo] = useState("");

  const [showLedgers, setShowLedgers] = useState(true);
  const [activeTab, setActiveTab] = useState("adLedger");
  const [challans, setChallans] = useState([]);
  const [loadingChallans, setLoadingChallans] = useState(false);

  const [pageNo, setPageNo] = useState(1);
  const [pageSize, setPageSize] = useState(100);

  const totalRecords = filteredLedger.length;

  const listOfLedger = useMemo(() => {
    return filteredLedger.slice(
      (pageNo - 1) * pageSize,
      pageNo * pageSize
    );
  }, [filteredLedger, pageNo, pageSize]);

  /** Net totals: outstanding uses latest row per fee code; received sums all historical rows. */
  const totalsNetFiltered = useMemo(() => {
    return computeLedgerTotals(filteredLedger);
  }, [filteredLedger]);

  const totalsNetAll = useMemo(() => {
    return computeLedgerTotals(fullLedger);
  }, [fullLedger]);

  const totalReceivedFromPaidChallans = useMemo(() => {
    return challans
      .filter((c) => c.Is_Paid)
      .reduce((sum, c) => sum + getChallanDueAmount(c), 0);
  }, [challans]);

  const latestPosting = useMemo(
    () => getLatestPostingSlice(fullLedger),
    [fullLedger]
  );

  const latestPostingTotals = useMemo(() => {
    return sumLedgerRows(latestPosting.rows);
  }, [latestPosting.rows]);

  /** Latest generated challan due amount — matches the fee voucher total. */
  const latestChallanReceivable = useMemo(() => {
    const fromChallan = getChallanReceivableAmount(challans);
    if (fromChallan != null) return fromChallan;
    return latestPostingTotals.receiveable;
  }, [challans, latestPostingTotals.receiveable]);

  const filteredChallanReceivable = useMemo(() => {
    const fromChallan = getChallanReceivableAmount(challans, {
      challanNo: filterChallanNo?.trim() || undefined,
      month: filterMonth || undefined,
      year: filterYear || undefined,
    });
    if (fromChallan != null) return fromChallan;
    if (filterChallanNo?.trim() || (filterMonth && filterYear)) {
      return sumLedgerRows(filteredLedger).receiveable;
    }
    return latestChallanReceivable;
  }, [
    challans,
    filterChallanNo,
    filterMonth,
    filterYear,
    filteredLedger,
    latestChallanReceivable,
  ]);

  const latestChallan = useMemo(() => {
    if (!challans.length) return null;
    return challans[0];
  }, [challans]);

  const latestUnpaidChallan = useMemo(() => {
    return challans.find((c) => !c.Is_Paid) || null;
  }, [challans]);

  const filterToLatestChallan = () => {
    if (!latestPosting.challanNo) {
      toast.info("No challan number on the latest posting.", {
        position: toast.POSITION.TOP_RIGHT,
      });
      return;
    }
    setFilterChallanNo(latestPosting.challanNo);
    setFilterMonth("");
    setFilterYear("");
    setPageNo(1);
    setFilteredLedger(
      fullLedger.filter(
        (item) => (item.Challan_No || "").trim() === latestPosting.challanNo
      )
    );
    setActiveTab("adLedger");
  };

  const loadLedger = useCallback(() => {
    if (!AccessKey || !UserID || !studentIdParam) {
      setLoading(false);
      return;
    }

    setLoading(true);
    const data = {
      AccessKey,
      UserID,
      MemberType: MemberType || "Admin",
      Student_ID: parseInt(studentIdParam, 10) || 0,
    };

    axios({
      method: "post",
      url: config.base_url + "ADLedger/GetADLedgerOfStudentAll",
      headers: { "Content-Type": "application/json" },
      data: data,
    })
      .then(function (response) {
        if (response.data.status_code === 1) {
          const records = response.data.ListofRecords || [];
          setFullLedger(records);
          setFilteredLedger(records);
          setPageNo(1);
        } else {
          setFullLedger([]);
          setFilteredLedger([]);
          toast.error(response.data.status_message || "No ledger data.", {
            position: toast.POSITION.TOP_RIGHT,
          });
        }
      })
      .catch(function () {
        toast.error("Network Error..", {
          position: toast.POSITION.TOP_RIGHT,
        });
        setFullLedger([]);
        setFilteredLedger([]);
      })
      .finally(() => setLoading(false));
  }, [AccessKey, UserID, MemberType, studentIdParam]);

  const loadChallans = useCallback(() => {
    if (!AccessKey || !UserID || !studentIdParam) {
      return;
    }

    setLoadingChallans(true);
    const data = {
      AccessKey,
      UserID,
      MemberType: MemberType || "Admin",
      Student_ID: parseInt(studentIdParam, 10) || 0,
    };

    axios({
      method: "post",
      url: config.base_url + "Student/GetChallansForStudent",
      headers: { "Content-Type": "application/json" },
      data: data,
    })
      .then(function (response) {
        if (response.data.status_code === 1) {
          setChallans(response.data.ListofRecords || []);
        } else {
          setChallans([]);
        }
      })
      .catch(function () {
        setChallans([]);
      })
      .finally(() => setLoadingChallans(false));
  }, [AccessKey, UserID, MemberType, studentIdParam]);

  useEffect(() => {
    document.title = "View Student Ledger";
    loadLedger();
    loadChallans();
  }, [loadLedger, loadChallans]);

  const handlePageChange = (page) => {
    setPageNo(page);
  };

  const applyFilters = () => {
    let filtered = [...fullLedger];

    if (filterChallanNo && filterChallanNo.trim().length > 0) {
      const challan = filterChallanNo.trim();
      filtered = filtered.filter((item) => item.Challan_No === challan);
    } else if (filterMonth && filterYear) {
      const selectedMonth = parseInt(filterMonth, 10);
      const y = filterYear;
      filtered = filtered.filter((item) => {
        if (!item.PostingDate || !moment(item.PostingDate).isValid()) {
          return false;
        }
        const month = moment(item.PostingDate).month() + 1;
        const year = moment(item.PostingDate).format("YYYY");
        return month === selectedMonth && year === y;
      });
    }

    setPageNo(1);
    setFilteredLedger(filtered);
  };

  const resetFilters = () => {
    setFilterMonth("");
    setFilterYear("");
    setFilterChallanNo("");
    setPageNo(1);
    setFilteredLedger(fullLedger);
  };

  const renderLedger = () => {
    if (listOfLedger != null && listOfLedger.length > 0) {
      return listOfLedger.map((item, index) => (
        <tr key={item.ID != null ? `id-${item.ID}-${index}` : index}>
          <td>{moment(item.PostingDate).format("MMM YYYY")}</td>
          <td>{item.Challan_No}</td>
          <td>{item.FeeCode}</td>
          <td>{item.Fee_Posted}</td>
          <td>{formatMoney(item.ConcessionAmount)}</td>
          <td>{formatMoney(item.ReceiveableFee)}</td>
          <td>{formatMoney(item.Arrears)}</td>
          <td>{item.Description}</td>
          <td>{formatMoney(item.ReceivedAmount)}</td>
          <td>
            {item.ReceivedDate
              ? moment(item.ReceivedDate).format("DD/MM/YYYY")
              : ""}
          </td>
          <td>{formatMoney(item.PendingFee)}</td>
        </tr>
      ));
    }
    return (
      <tr>
        <td colSpan="11">
          <h4
            className="mt-3"
            style={{ fontSize: "15px", color: "rgba(0,0,0,0.6)" }}
          >
            No Data Found
          </h4>
        </td>
      </tr>
    );
  };

  return (
    <>
      <StudentMenu />

      <ToastContainer />

      <div className="container body-content">
        <div className="page-head">
          <div className="container">
            <div className="row align-items-center">
              <div className="col-md-6">
                <nav aria-label="breadcrumb">
                  <ol className="breadcrumb">
                    <li className="breadcrumb-item">
                      <NavLink to="/Dashboard">Dashboard</NavLink>
                    </li>
                    <li className="breadcrumb-item">
                      <NavLink to="/student/studentdashboard">Students</NavLink>
                    </li>
                    <li className="breadcrumb-item active" aria-current="page">
                      Student Ledger
                    </li>
                  </ol>
                </nav>
                <div className="page-title">Student Ledger</div>
                <p className="text-muted small mb-0">
                  AD ledger is the source of truth. Unpaid amounts roll into the
                  next month&apos;s challan as <strong>Arrears</strong> on each
                  fee line; net outstanding uses the latest row per fee code so
                  nothing is counted twice.
                </p>
              </div>
              <div className="col-md-6">
                <div className="page-right-actions">
                  <button
                    onClick={() => {
                      setShowLedgers((prevIsActive) => !prevIsActive);
                    }}
                    className="button button-white button-icon"
                  >
                    {showLedgers ? "Hide Detail" : "Show Detail"}
                    <i className="ri-filter-3-line"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div
          className={`collapse ${showLedgers ? "show" : ""} `}
          id="collapseFilteres"
        >
          <ShowStudent StudentID={studentIdParam} />
        </div>
        <div className="page-content mt-3">
          <div className="container">
            <div className="row g-3 mb-3">
              <div className="col-lg-4">
                <div className="card border-0 shadow-sm h-100 border-start border-4 border-warning">
                  <div className="card-body">
                    <div className="text-muted small">Net outstanding (all fee heads)</div>
                    <div className="h3 mb-1 text-warning">
                      {loading ? "…" : formatMoney(totalsNetAll.outstanding)}
                    </div>
                    <div className="small text-muted">
                      Latest AD ledger row per fee code — arrears rolled into a
                      newer month are not counted twice.
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-lg-4">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-body">
                    <div className="text-muted small">Latest fee posting</div>
                    <div className="fw-semibold mb-1">
                      {latestPosting.monthLabel || "—"}
                      {latestPosting.challanNo ? (
                        <span className="text-muted fw-normal">
                          {" "}
                          · Challan {latestPosting.challanNo}
                        </span>
                      ) : null}
                    </div>
                    <div className="small">
                      Current month fee:{" "}
                      <b>
                        {formatMoney(
                          latestPostingTotals.feePosted -
                            latestPostingTotals.concession
                        )}
                      </b>
                    </div>
                    <div className="small">
                      Arrears in this challan:{" "}
                      <b>{formatMoney(latestPostingTotals.arrears)}</b>
                    </div>
                    <div className="small">
                      Challan receivable:{" "}
                      <b>{formatMoney(latestPostingTotals.receiveable)}</b>
                      {" · "}
                      Pending:{" "}
                      <b className="text-warning">
                        {formatMoney(latestPostingTotals.pending)}
                      </b>
                    </div>
                    {latestPosting.challanNo ? (
                      <button
                        type="button"
                        className="btn btn-link btn-sm ps-0 mt-1"
                        onClick={filterToLatestChallan}
                      >
                        Show lines for this challan
                      </button>
                    ) : null}
                  </div>
                </div>
              </div>
              <div className="col-lg-4">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-body">
                    <div className="text-muted small">Latest challan (fee voucher)</div>
                    {latestChallan ? (
                      <>
                        <div className="fw-semibold mb-1">
                          {latestChallan.Challan_No}
                          <span
                            className={`badge ms-2 ${
                              latestChallan.Is_Paid
                                ? "bg-success"
                                : "bg-warning text-dark"
                            }`}
                          >
                            {latestChallan.Is_Paid ? "Paid" : "Unpaid"}
                          </span>
                        </div>
                        <div className="small text-muted">
                          {moment()
                            .month((latestChallan.Month || 1) - 1)
                            .format("MMMM")}{" "}
                          {latestChallan.Year}
                        </div>
                        <div className="small">
                          Due:{" "}
                          <b>{formatMoney(getChallanDueAmount(latestChallan))}</b>
                          {latestChallan.Due_Date
                            ? ` (by ${moment(latestChallan.Due_Date).format("DD/MM/YYYY")})`
                            : ""}
                        </div>
                        {latestUnpaidChallan &&
                        latestChallan &&
                        latestUnpaidChallan.Challan_No !==
                          latestChallan.Challan_No ? (
                          <div className="small mt-2 text-danger">
                            Unpaid challan: {latestUnpaidChallan.Challan_No} —{" "}
                            {formatMoney(getChallanDueAmount(latestUnpaidChallan))}
                          </div>
                        ) : null}
                      </>
                    ) : (
                      <div className="text-muted">No challan on file</div>
                    )}
                    <div className="small mt-2 text-muted">
                      Total received (lifetime):{" "}
                      <b className="text-success">
                        {loading ? "…" : formatMoney(totalsNetAll.totalReceived)}
                      </b>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="card">
              <div className="card-body">
                <ul className="nav nav-pills mb-3">
                  <li className="nav-item">
                    <button
                      type="button"
                      className={`nav-link ${activeTab === "adLedger" ? "active" : ""}`}
                      onClick={() => setActiveTab("adLedger")}
                    >
                      AD Ledger View
                    </button>
                  </li>
                  <li className="nav-item ms-2">
                    <button
                      type="button"
                      className={`nav-link ${activeTab === "challan" ? "active" : ""}`}
                      onClick={() => setActiveTab("challan")}
                    >
                      Challan View
                    </button>
                  </li>
                </ul>

                {activeTab === "adLedger" ? (
                  <>
                <div className="row mb-3">
                  <div className="col-md-3">
                    <label className="form-label">Filter Month</label>
                    <select
                      className="form-select"
                      value={filterMonth}
                      onChange={(e) => setFilterMonth(e.target.value)}
                    >
                      <option value="">All</option>
                      <MonthsDropDown />
                    </select>
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">Filter Year</label>
                    <select
                      className="form-select"
                      value={filterYear}
                      onChange={(e) => setFilterYear(e.target.value)}
                    >
                      <option value="">All</option>
                      <YearsDropDown />
                    </select>
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">Challan No</label>
                    <input
                      type="text"
                      className="form-control"
                      value={filterChallanNo}
                      onChange={(e) => setFilterChallanNo(e.target.value)}
                      placeholder="Exact challan number"
                    />
                  </div>
                  <div className="col-md-3 d-flex align-items-end gap-2 flex-wrap">
                    <button
                      type="button"
                      className="button button-primary"
                      onClick={applyFilters}
                    >
                      Apply
                    </button>
                    <button
                      type="button"
                      className="button button-light"
                      onClick={resetFilters}
                    >
                      Reset
                    </button>
                  </div>
                </div>

                <table className="table table-theme">
                  <thead>
                    <tr>
                      <th colSpan="11" className="text-end">
                        {totalRecords} row(s) in current filter (page{" "}
                        {pageNo} of{" "}
                        {Math.max(1, Math.ceil(totalRecords / pageSize) || 1)})
                      </th>
                    </tr>
                    <tr>
                      <th>Fee Month</th>
                      <th>Challan No</th>
                      <th>Fee Code</th>
                      <th>Fee Posted</th>
                      <th>Concession Amount</th>
                      <th>Receiveable Fee</th>
                      <th>Arrears</th>
                      <th>Description</th>
                      <th>Received Amount</th>
                      <th>Received Date</th>
                      <th>Pending Fee</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td className="text-center" colSpan="11">
                          <Spinner animation="border" role="status"></Spinner>
                        </td>
                      </tr>
                    ) : (
                      renderLedger()
                    )}
                    {!loading && filteredLedger.length > 0 && (
                      <tr className="table-light">
                        <td colSpan={5} className="text-end small text-muted">
                          <b>
                            Net totals — receivable: latest/matched challan;
                            outstanding: latest per fee; received: sum of all rows
                          </b>
                        </td>
                        <td>
                          <b>{formatMoney(filteredChallanReceivable)}</b>
                        </td>
                        <td>
                          <b>{formatMoney(totalsNetFiltered.currentArrears)}</b>
                        </td>
                        <td></td>
                        <td>
                          <b>{formatMoney(totalsNetFiltered.totalReceived)}</b>
                        </td>
                        <td></td>
                        <td>
                          <b>{formatMoney(totalsNetFiltered.outstanding)}</b>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
                {totalRecords > pageSize && (
                  <div className="pagination-wrap">
                    <div className="row align-items-center">
                      <div className="col-md-2 form_default">
                        <div className="d-flex align-items-center">
                          <label className="me-2" style={{ fontSize: "15px" }}>
                            Page Size
                          </label>
                          <div className="form-group">
                            <select
                              className="form-select"
                              style={{ width: "100px" }}
                              value={pageSize}
                              onChange={(e) => {
                                setPageNo(1);
                                setPageSize(parseInt(e.target.value, 10));
                              }}
                            >
                              <option value="10">10</option>
                              <option value="20">20</option>
                              <option value="50">50</option>
                              <option value="100">100</option>
                              <option value="500">500</option>
                              <option value="1000">1000</option>
                            </select>
                          </div>
                        </div>
                      </div>

                      <Pagination
                        pageNo={pageNo}
                        pageSize={pageSize}
                        totalRecords={totalRecords}
                        onPageChange={handlePageChange}
                      />
                    </div>
                  </div>
                )}
                  </>
                ) : (
                  <>
                    <div className="row g-3 mb-3">
                      <div className="col-md-4">
                        <div className="card border-0 bg-light">
                          <div className="card-body py-2">
                            <div className="small text-muted">
                              Current receivable (latest challan)
                              {latestChallan ? (
                                <span className="d-block">
                                  {latestChallan.Challan_No}
                                </span>
                              ) : null}
                            </div>
                            <div className="fw-bold">{formatMoney(latestChallanReceivable)}</div>
                          </div>
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className="card border-0 bg-light">
                          <div className="card-body py-2">
                            <div className="small text-muted">
                              Total received (all ledger lines)
                            </div>
                            <div className="fw-bold text-success">{formatMoney(totalsNetAll.totalReceived)}</div>
                            {totalReceivedFromPaidChallans > 0 &&
                            totalReceivedFromPaidChallans !== totalsNetAll.totalReceived ? (
                              <div className="small text-muted mt-1">
                                Paid challans: {formatMoney(totalReceivedFromPaidChallans)}
                              </div>
                            ) : null}
                          </div>
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className="card border-0 bg-light">
                          <div className="card-body py-2">
                            <div className="small text-muted">
                              Outstanding (latest per fee)
                            </div>
                            <div className="fw-bold text-warning">{formatMoney(totalsNetAll.outstanding)}</div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <p className="small text-muted mb-2">
                      Current receivable is the due amount on the latest generated
                      challan (fee voucher). Outstanding uses the latest AD ledger row
                      per fee head. Total received sums every ledger line.
                    </p>
                    <table className="table table-theme">
                      <thead>
                        <tr>
                          <th colSpan="9" className="text-end">
                            {challans.length} challan(s) found
                          </th>
                        </tr>
                        <tr>
                          <th>Month</th>
                          <th>Year</th>
                          <th>Challan No</th>
                          <th>Dated</th>
                          <th>Due Date</th>
                          <th>Due Amount</th>
                          <th>Status</th>
                          <th>Received Date</th>
                          <th>Received Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {loadingChallans ? (
                          <tr>
                            <td colSpan="9" className="text-center">
                              <Spinner animation="border" role="status" />
                            </td>
                          </tr>
                        ) : challans.length === 0 ? (
                          <tr>
                            <td colSpan="9">
                              <h4
                                className="mt-3"
                                style={{ fontSize: "15px", color: "rgba(0,0,0,0.6)" }}
                              >
                                No challan data found
                              </h4>
                            </td>
                          </tr>
                        ) : (
                          <>
                            {challans.map((item, index) => {
                              const dueAmount = getChallanDueAmount(item);
                              const receivedAmount = item.Is_Paid ? dueAmount : 0;
                              const isLatest =
                                latestChallan &&
                                item.Challan_No === latestChallan.Challan_No;
                              const isUnpaidHighlight =
                                latestUnpaidChallan &&
                                item.Challan_No === latestUnpaidChallan.Challan_No;
                              return (
                                <tr
                                  key={`${item.Challan_No || "ch"}-${index}`}
                                  className={
                                    isUnpaidHighlight
                                      ? "table-warning"
                                      : isLatest
                                        ? "table-light"
                                        : ""
                                  }
                                >
                                  <td>{item.Month}</td>
                                  <td>{item.Year}</td>
                                  <td>{item.Challan_No}</td>
                                  <td>{item.Dated ? moment(item.Dated).format("DD/MM/YYYY") : ""}</td>
                                  <td>{item.Due_Date ? moment(item.Due_Date).format("DD/MM/YYYY") : ""}</td>
                                  <td>{formatMoney(dueAmount)}</td>
                                  <td>
                                    <span className={`badge ${item.Is_Paid ? "bg-success" : "bg-warning text-dark"}`}>
                                      {item.Is_Paid ? "Paid" : "Unpaid"}
                                    </span>
                                  </td>
                                  <td>
                                    {item.Is_Paid && item.Received_Date
                                      ? moment(item.Received_Date).format("DD/MM/YYYY")
                                      : ""}
                                  </td>
                                  <td>{formatMoney(receivedAmount)}</td>
                                </tr>
                              );
                            })}
                            <tr className="table-light">
                              <td colSpan="5" className="text-end small text-muted">
                                <b>Sum of challan due amounts</b> (historical; may
                                include arrears already rolled forward)
                              </td>
                              <td>
                                <b>
                                  {formatMoney(
                                    challans.reduce(
                                      (s, c) => s + getChallanDueAmount(c),
                                      0
                                    )
                                  )}
                                </b>
                              </td>
                              <td colSpan="3"></td>
                            </tr>
                            <tr className="table-warning">
                              <td colSpan="5" className="text-end small">
                                <b>Net outstanding (AD ledger — use this)</b>
                              </td>
                              <td>
                                <b>{formatMoney(totalsNetAll.outstanding)}</b>
                              </td>
                              <td colSpan="2" className="small text-muted">
                                Received (all lines): {formatMoney(totalsNetAll.totalReceived)}
                              </td>
                              <td></td>
                            </tr>
                          </>
                        )}
                      </tbody>
                    </table>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default StudentLedger2;
